import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  ModalProps,
  Stack,
  useToast,
  Text,
  Box,
  Flex,
  HStack,
  VStack,
  Divider,
  Button as ChakraButton,
  useDisclosure,
} from '@chakra-ui/react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { QRCodeCanvas } from 'qrcode.react';
import {
  DisketteIcon,
  EnvelopeIcon,
  FacebookIcon,
  LinkedinIcon,
  LinkIcon,
  MessageIcon,
  OpenFolderIcon,
  TelegramIcon,
  UserFrameIcon,
  WhatsappIcon,
  XLogoIcon,
} from 'components/Icons';
import { MeContext } from 'auth/AuthContextProvider';
import { useContext, useRef } from 'react';

export type Props = {
  onOpenCreateContactModal?: () => void;
  onOpenSendLinkModal?: () => void;
  onOpenNewDossierTunnel?: () => void;
} & Omit<ModalProps, 'children'>;

export const ActionsModal = ({
  onOpenCreateContactModal,
  onOpenSendLinkModal,
  onOpenNewDossierTunnel,
  ...props
}: Props) => {
  const toast = useToast();
  const me = useContext(MeContext);
  const qrRef = useRef<HTMLDivElement>(null);
  const qrModal = useDisclosure();

  const isSimply = !!me.utilisateur?.organisation?.mode_simply;
  const hasApporteurCode = me.utilisateur?.apporteur_code !== null;
  const canShowFicheContact = !!me.utilisateur?.organisation?.autoriser_fiche_contact;
  const canShowIobsp = !!me.utilisateur?.organisation?.autoriser_iobsp;
  const lienPersonnel = `${me?.marque?.url_tunnel}?apcode=${me?.utilisateur?.apporteur_code}`;

  // Partage du lien personnel sur les réseaux sociaux / messageries.
  const partageTexte = `Réalisez votre simulation de financement en autonomie : ${lienPersonnel}`;
  const lienEncode = encodeURIComponent(lienPersonnel);
  const texteEncode = encodeURIComponent(partageTexte);
  const sujetEncode = encodeURIComponent('Votre simulation de financement');

  const reseauxPartage = [
    {
      label: 'Partager sur WhatsApp',
      icon: WhatsappIcon,
      color: '#25D366',
      href: `https://wa.me/?text=${texteEncode}`,
    },
    {
      label: 'Partager par SMS',
      icon: MessageIcon,
      color: '#7C3AED',
      href: `sms:?&body=${texteEncode}`,
    },
    {
      label: 'Partager par email',
      icon: EnvelopeIcon,
      color: '#EA4335',
      href: `mailto:?subject=${sujetEncode}&body=${texteEncode}`,
    },
    {
      label: 'Partager sur Facebook',
      icon: FacebookIcon,
      color: '#1877F2',
      href: `https://www.facebook.com/sharer/sharer.php?u=${lienEncode}`,
    },
    {
      label: 'Partager sur Telegram',
      icon: TelegramIcon,
      color: '#0088CC',
      href: `https://t.me/share/url?url=${lienEncode}&text=${texteEncode}`,
    },
    {
      label: 'Partager sur LinkedIn',
      icon: LinkedinIcon,
      color: '#0A66C2',
      href: `https://www.linkedin.com/sharing/share-offline/?url=${lienEncode}`,
    },
    {
      label: 'Partager sur X',
      icon: XLogoIcon,
      color: '#000000',
      href: `https://twitter.com/intent/tweet?text=${texteEncode}`,
    },
  ];

  // Téléchargement du QR code (canvas -> image PNG).
  const handleDownloadQrCode = () => {
    const canvas = qrRef.current?.querySelector('canvas');
    if (!canvas) return;

    try {
      const url = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.href = url;
      link.download = 'mon-lien-personnel-qr-code.png';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (e) {
      toast({
        title: 'Impossible de télécharger le QR code, veuillez réessayer.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <>
      <Modal {...props} scrollBehavior="inside" size="4xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader />
          <ModalCloseButton />
          <ModalBody pb={{ base: 7, lg: 12 }} pt={{ base: 0, lg: 6 }}>
            <VStack spacing={2} mb={{ base: 6, lg: 10 }}>
              <Text as="h2" fontSize="2xl" fontWeight="bold" color="teal.900" textAlign="center">
                Démarrer un nouveau projet
              </Text>
              {!isSimply && (
                <Text fontSize="sm" color="gray.600" textAlign="center">
                  Choisissez comment lancer la simulation
                </Text>
              )}
            </VStack>

            {!isSimply && (
              <>
                <Stack
                  direction={{ base: 'column', lg: 'row' }}
                  spacing={4}
                  mb={{ base: 8, lg: 10 }}
                >
                  <ActionCard
                    icon={LinkIcon}
                    label="Envoi d'un lien pour simulation"
                    onClick={onOpenSendLinkModal}
                    isDisabled={!hasApporteurCode}
                  />
                  {canShowFicheContact && (
                    <ActionCard
                      icon={UserFrameIcon}
                      label="Création d'une fiche contact"
                      onClick={onOpenCreateContactModal}
                    />
                  )}
                  {canShowIobsp && (
                    <ActionCard
                      icon={OpenFolderIcon}
                      label="Simulation pendant rendez-vous"
                      onClick={onOpenNewDossierTunnel}
                      isDisabled={!hasApporteurCode}
                    />
                  )}
                </Stack>

                <Flex align="center" mb={{ base: 6, lg: 8 }}>
                  <Divider borderColor="gray.200" />
                  <Text px={4} fontSize="sm" color="gray.500" fontWeight="semibold">
                    OU
                  </Text>
                  <Divider borderColor="gray.200" />
                </Flex>
              </>
            )}

            <Box>
              <HStack spacing={3} mb={3}>
                <Flex
                  align="center"
                  justify="center"
                  w="32px"
                  h="32px"
                  borderRadius="full"
                  bg="teal.50"
                >
                  <LinkIcon boxSize="16px" color="teal.500" />
                </Flex>
                <Text fontSize="lg" fontWeight="bold" color="teal.900">
                  Mon lien personnel
                </Text>
              </HStack>

              {hasApporteurCode ? (
                <>
                  <Stack
                    direction={{ base: 'column', sm: 'row' }}
                    align={{ base: 'stretch', sm: 'center' }}
                    spacing={5}
                    mb={5}
                  >
                    <Text fontSize="sm" color="gray.700" lineHeight="tall" flex={1}>
                      Copiez ce lien et envoyez-le à votre client par mail ou SMS.
                      <br />
                      Il pourra réaliser sa simulation en autonomie.
                      <br />
                      Le dossier apparaîtra ensuite dans votre liste, prêt à être traité.
                    </Text>

                    <Flex
                      as="button"
                      type="button"
                      onClick={qrModal.onOpen}
                      flexShrink={0}
                      align="center"
                      borderWidth={1}
                      borderColor="gray.200"
                      borderRadius="lg"
                      bg="white"
                      p={3}
                      transition="all 0.15s ease"
                      _hover={{
                        borderColor: 'teal.500',
                        bg: 'teal.50',
                        transform: 'translateY(-1px)',
                      }}
                      _focus={{ outline: 'none', boxShadow: 'teal' }}
                    >
                      <Box borderRadius="md" overflow="hidden" lineHeight={0}>
                        <QRCodeCanvas
                          value={lienPersonnel}
                          size={56}
                          level="M"
                          bgColor="#ffffff"
                          fgColor="#153e35"
                        />
                      </Box>
                      <VStack spacing={0} align="start" ml={3} pr={2}>
                        <Text fontSize="sm" fontWeight="semibold" color="teal.900">
                          QR code
                        </Text>
                        <Text fontSize="xs" color="gray.500">
                          Cliquez pour l'afficher
                        </Text>
                      </VStack>
                    </Flex>
                  </Stack>

                  <Flex
                    direction={{ base: 'column', sm: 'row' }}
                    align="stretch"
                    borderWidth={1}
                    borderColor="gray.200"
                    borderRadius="md"
                    overflow="hidden"
                  >
                    <Box flex={1} px={4} py={3} bg="gray.50" minWidth={0}>
                      <Text
                        fontSize="sm"
                        color="teal.900"
                        fontFamily="mono"
                        isTruncated
                        title={lienPersonnel}
                      >
                        {lienPersonnel}
                      </Text>
                    </Box>
                    <CopyToClipboard
                      text={lienPersonnel}
                      onCopy={() => {
                        toast({
                          title: 'Lien personnel copié dans votre presse papier.',
                          status: 'success',
                          duration: 3000,
                          isClosable: true,
                        });
                      }}
                    >
                      <ChakraButton
                        bg="teal.500"
                        color="white"
                        borderRadius={0}
                        px={6}
                        minH="48px"
                        fontSize="sm"
                        fontWeight="semibold"
                        leftIcon={<LinkIcon boxSize="16px" />}
                        _hover={{ bg: 'green.500' }}
                        _active={{ bg: 'green.500' }}
                        _focus={{ boxShadow: 'teal' }}
                      >
                        Copier le lien
                      </ChakraButton>
                    </CopyToClipboard>
                  </Flex>

                  <Box mt={6}>
                    <Text fontSize="sm" fontWeight="semibold" color="teal.900" mb={3}>
                      Ou partagez-le directement
                    </Text>
                    <HStack spacing={3} flexWrap="wrap" justify="center">
                      {reseauxPartage.map((reseau) => {
                        const isExternal = reseau.href.startsWith('http');
                        return (
                          <ChakraButton
                            key={reseau.label}
                            as="a"
                            href={reseau.href}
                            aria-label={reseau.label}
                            title={reseau.label}
                            target={isExternal ? '_blank' : undefined}
                            rel={isExternal ? 'noopener noreferrer' : undefined}
                            display="inline-flex"
                            alignItems="center"
                            justifyContent="center"
                            bg={reseau.color}
                            color="white"
                            borderRadius="full"
                            boxSize="44px"
                            minW="44px"
                            p={0}
                            _hover={{ opacity: 0.85 }}
                            _active={{ opacity: 0.7 }}
                            _focus={{ boxShadow: 'teal' }}
                          >
                            <reseau.icon boxSize="20px" />
                          </ChakraButton>
                        );
                      })}
                    </HStack>
                  </Box>
                </>
              ) : (
                <Box bg="red.50" borderWidth={1} borderColor="red.100" borderRadius="md" p={4}>
                  <Text color="red.500" fontSize="md" fontWeight="semibold" textAlign="center">
                    Vous ne possédez pas de code apporteur, contactez votre courtier pour en obtenir
                    un.
                  </Text>
                </Box>
              )}
            </Box>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Affichage du QR code en grand + téléchargement */}
      <Modal isOpen={qrModal.isOpen} onClose={qrModal.onClose} isCentered size="sm">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader textAlign="center" color="teal.900" pb={2}>
            Mon QR code
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={8}>
            <VStack spacing={5}>
              <Text fontSize="sm" color="gray.600" textAlign="center">
                Faites scanner ce QR code à votre client pour qu'il accède directement à sa
                simulation.
              </Text>
              <Box
                ref={qrRef}
                p={4}
                bg="white"
                borderWidth={1}
                borderColor="gray.200"
                borderRadius="lg"
                boxShadow="sm"
              >
                <QRCodeCanvas
                  value={lienPersonnel}
                  size={240}
                  level="H"
                  bgColor="#ffffff"
                  fgColor="#153e35"
                  includeMargin
                  imageSettings={{
                    src: '/logo-monemprunt.svg',
                    width: 76,
                    height: 27,
                    excavate: true,
                  }}
                />
              </Box>
              <ChakraButton
                w="full"
                bg="teal.500"
                color="white"
                fontSize="sm"
                fontWeight="semibold"
                leftIcon={<DisketteIcon boxSize="16px" />}
                onClick={handleDownloadQrCode}
                _hover={{ bg: 'green.500' }}
                _active={{ bg: 'green.500' }}
                _focus={{ boxShadow: 'teal' }}
              >
                Télécharger le QR code
              </ChakraButton>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

type ActionCardProps = {
  icon: typeof LinkIcon;
  label: string;
  onClick?: () => void;
  isDisabled?: boolean;
};

const ActionCard = ({ icon: Icon, label, onClick, isDisabled }: ActionCardProps) => (
  <Box
    as="button"
    type="button"
    onClick={onClick}
    disabled={isDisabled}
    flex={1}
    borderWidth={1}
    borderColor="gray.200"
    borderRadius="md"
    p={6}
    bg="white"
    cursor={isDisabled ? 'not-allowed' : 'pointer'}
    opacity={isDisabled ? 0.5 : 1}
    transition="all 0.15s ease"
    _hover={
      !isDisabled ? { borderColor: 'teal.500', bg: 'teal.50', transform: 'translateY(-2px)' } : {}
    }
    _focus={{ outline: 'none', boxShadow: 'teal' }}
  >
    <VStack spacing={4}>
      <Flex align="center" justify="center" w="56px" h="56px" borderRadius="full" bg="teal.50">
        <Icon boxSize="28px" color="teal.500" />
      </Flex>
      <Text color="teal.900" fontSize="sm" fontWeight="semibold" textAlign="center">
        {label}
      </Text>
    </VStack>
  </Box>
);
